import React, { useState, useEffect } from 'react';
import { Flower, RefreshCw, Trophy, Heart } from 'lucide-react';

// Helper to generate primes up to n
const getPrimesUpTo = (n) => {
  const sieve = Array(n + 1).fill(true);
  sieve[0] = false;
  sieve[1] = false;
  for (let i = 2; i * i <= n; i++) {
    if (sieve[i]) {
      for (let j = i * i; j <= n; j += i) {
        sieve[j] = false;
      }
    }
  }
  return sieve.map((isPrime, i) => isPrime ? i : null).filter(n => n);
};

const POSSIBLE_PRIMES = getPrimesUpTo(50); // Pool of primes to choose from

const GoldbachsGarden = () => {
  const [target, setTarget] = useState(0);
  const [selectedPrimes, setSelectedPrimes] = useState([]);
  const [score, setScore] = useState(0);
  const [message, setMessage] = useState("Pick two flowers that sum to the target!");
  const [gameState, setGameState] = useState('playing'); // playing, victory
  const [animating, setAnimating] = useState(false);

  // Initialize round
  useEffect(() => {
    generateNewRound();
  }, []);

  const generateNewRound = () => {
    // Generate an even number that is definitely a sum of two primes in our pool
    // 1. Pick two random primes
    const p1 = POSSIBLE_PRIMES[Math.floor(Math.random() * POSSIBLE_PRIMES.length)];
    const p2 = POSSIBLE_PRIMES[Math.floor(Math.random() * POSSIBLE_PRIMES.length)];
    setTarget(p1 + p2);
    setSelectedPrimes([]);
    setMessage("Pick two flowers that sum to the target!");
    setAnimating(false);
  };

  const handleFlowerClick = (prime) => {
    if (gameState !== 'playing' || animating) return;
    
    // Toggle selection if already selected (undo)
    if (selectedPrimes.includes(prime)) {
        // Only if it's the specific instance? Simplified: Just remove one instance of this value
        // But since we might pick same prime twice (e.g. 5+5=10), we need to handle index or just value carefully.
        // For simplicity, let's just allow deselecting the last one or clearing.
        // Let's go with: Click to add. If 2 selected, we evaluate.
        // If clicking one that matches the first selected, currently it just adds as second.
        
        // Actually, let's just check bounds.
        // If we want to allow deselecting, we need to know which one.
        // Let's keep it simple: Click adds to selection.
    }

    const newSelection = [...selectedPrimes, prime];
    setSelectedPrimes(newSelection);

    if (newSelection.length === 2) {
      setAnimating(true);
      checkSum(newSelection[0], newSelection[1]);
    }
  };

  const checkSum = (p1, p2) => {
    const sum = p1 + p2;
    if (sum === target) {
      // Success
      setMessage(`Correct! ${p1} + ${p2} = ${target}`);
      setTimeout(() => {
        setScore(s => s + 1);
        generateNewRound();
      }, 1500);
    } else {
      // Failure
      setMessage(`Oops! ${p1} + ${p2} = ${sum}. Try again!`);
      setTimeout(() => {
        setSelectedPrimes([]);
        setMessage("Pick two flowers that sum to the target!");
        setAnimating(false);
      }, 1500);
    }
  };

  return (
    <div className="min-h-screen bg-green-50 flex flex-col items-center p-6 font-sans">
      {/* Header */}
      <div className="w-full max-w-4xl flex justify-between items-center mb-8 relative">
        <button
          onClick={() => (window.location.href = "/games")}
          className="absolute -top-12 left-0 flex items-center text-green-700 hover:text-green-900 font-medium"
        >
          <div className="mr-1">←</div> Back to Games
        </button>
        <div>
           <h1 className="text-3xl font-bold text-green-800 flex items-center gap-2">
             <Flower className="text-pink-500" /> Goldbach's Garden
           </h1>
           <p className="text-green-600">Every even number is the sum of two primes.</p>
        </div>
        <div className="text-2xl font-bold text-green-700 bg-white px-6 py-2 rounded-full shadow-sm border border-green-200">
           Score: {score}
        </div>
      </div>

      {/* Main Game Area */}
      <div className="bg-white p-8 rounded-3xl shadow-xl w-full max-w-2xl text-center border-4 border-green-100 relative overflow-hidden">
         
         {/* Introduction / Instruction */}
         <div className="mb-8">
            <h2 className="text-gray-500 uppercase tracking-widest text-sm font-bold mb-2">Target Number</h2>
            <div className={`text-6xl font-black text-slate-800 transition-all duration-500 ${animating && selectedPrimes.reduce((a,b)=>a+b,0) === target ? 'scale-110 text-green-600' : ''}`}>
                {target}
            </div>
            <div className="h-8 mt-4 text-lg font-medium text-pink-600 animate-pulse">
                {message}
            </div>
         </div>

         {/* Garden Grid */}
         <div className="grid grid-cols-5 sm:grid-cols-6 gap-4 justify-items-center">
            {POSSIBLE_PRIMES.map((prime) => {
                const isSelected = selectedPrimes.includes(prime);
                // If same prime selected twice, we need to highlight nicely.
                // Since includes check value, simple boolean works for now. 
                // A better way for exact UI if selecting 5 twice:
                // But simplified for MVP.
                
                return (
                    <button
                        key={prime}
                        onClick={() => handleFlowerClick(prime)}
                        disabled={animating || (selectedPrimes.length >= 2)}
                        className={`
                            relative w-16 h-16 rounded-full flex items-center justify-center text-xl font-bold transition-all duration-300
                            ${isSelected 
                                ? 'bg-pink-500 text-white scale-110 shadow-lg ring-4 ring-pink-200' 
                                : 'bg-green-100 text-green-800 hover:bg-green-200 hover:scale-105'
                            }
                        `}
                    >
                        {prime}
                        {/* Little flower petals decoration */}
                        <Flower className={`absolute -top-1 -right-1 w-6 h-6 ${isSelected ? 'text-yellow-300' : 'text-pink-300'} opacity-50`} />
                    </button>
                )
            })}
         </div>

         {/* Selected Equation Display */}
         <div className="mt-8 p-4 bg-slate-50 rounded-xl min-h-[80px] flex items-center justify-center gap-4 text-2xl font-bold text-slate-600">
             {selectedPrimes[0] ? <span className="text-pink-600">{selectedPrimes[0]}</span> : <span className="text-slate-300">?</span>}
             <span>+</span>
             {selectedPrimes[1] ? <span className="text-pink-600">{selectedPrimes[1]}</span> : <span className="text-slate-300">?</span>}
             <span>=</span>
             <span>{selectedPrimes.reduce((a, b) => a + b, 0) || '?'}</span>
         </div>
      </div>

    </div>
  );
};

export default GoldbachsGarden;
